// Athena lists every imaging result TWICE across our two document feeds:
//   - as the imaging result itself: a FHIR DiagnosticReport
//     (a-9928.clinicalresult-876978) whose Athena imaging-result id is
//     2961676 — procedures/get returns the FHIR id, the mirror imagingresult
//     list returns both — and
//   - in its FHIR DocumentReference feed, which the mirror's clinicaldocument
//     list reads, as a "report" whose id embeds the imaging-result id:
//     a-9928.cardiologyReport-2961676 / a-9928.radiologyReport-2961676.
// Any view that merges imaging + clinical lists (Documents tab "All", the
// CareIQ timeline) therefore rendered each ECG / ultrasound / X-ray twice —
// "Electrocardiogram · Imaging" plus "Cardiology Report · Clinical" — ever
// since the clinical list started reading DocumentReference (March 2026).
// QA 2026-09-30 (patients 13616 / 13618).
//
// The report row is dropped ONLY when its imaging twin is in the same view.
// ~80 cached report rows have no imaging twin (different department, or not
// in the imaging list); those stay visible so no document disappears.
const REPORT_TWIN_RE = /^a-\d+\.(?:cardiology|radiology)Report-(\d+)$/;

// Imaging-result id a clinical "report" row duplicates, or null.
export const imagingTwinIdOf = (doc) => {
  const m = REPORT_TWIN_RE.exec(String(doc?.clinicaldocumentid ?? ""));
  return m ? m[1] : null;
};

// Athena imaging-result ids (as strings) of the imaging rows on screen.
// Rows from the mirror imaging list carry the numeric id (2961679) plus
// fhir_resource_id; rows from procedures/get carry ONLY the FHIR
// DiagnosticReport id (a-9928.clinicalresult-876977) in imagingresultid.
// `idSource` (the mirror imaging list) pairs the two, so a procedures/get
// row can be matched to the numeric id its clinical twin embeds. A row that
// can't be paired simply keeps its twin.
export const imagingIdSet = (imagingDocs, idSource = []) => {
  const fhirToAthena = new Map();
  (idSource || []).forEach((m) => {
    const num = m?.imagingresultid;
    if (m?.fhir_resource_id && num !== undefined && num !== null && /^\d+$/.test(String(num))) {
      fhirToAthena.set(String(m.fhir_resource_id), String(num));
    }
  });
  const ids = new Set();
  (imagingDocs || []).forEach((d) => {
    const raw = d?.imagingresultid;
    if (raw === undefined || raw === null || String(raw) === "") return;
    const id = String(raw);
    if (/^\d+$/.test(id)) ids.add(id);
    const paired = fhirToAthena.get(String(d?.fhir_resource_id ?? id)) || fhirToAthena.get(id);
    if (paired) ids.add(paired);
  });
  return ids;
};

// True when `doc` is a clinical report row whose imaging twin is in `ids`.
export const isImagingReportTwin = (doc, ids) => {
  if (!ids || !ids.size) return false;
  const twin = imagingTwinIdOf(doc);
  return !!twin && ids.has(twin);
};

// Clinical rows minus the ones that duplicate an imaging row being shown.
export const dropImagingReportTwins = (clinicalDocs, imagingDocs, idSource = []) => {
  const ids = imagingIdSet(imagingDocs, idSource);
  if (!ids.size) return clinicalDocs || [];
  return (clinicalDocs || []).filter((d) => !isImagingReportTwin(d, ids));
};
